import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import _ from "lodash";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

// Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";

// Services
import AppIconapi from "Services/AppIcon";
import ServiceFareApi from "Services/Servicefare";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AppIconUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );
  const [details, setDetails] = useState(null);
  const [serviceList, setServiceList] = useState([]);

  useEffect(() => {
    if (openDialog) {
      ServiceFareApi.getServicesList({
        limit: "unlimited",
        ...(demoaccount_id ? { demoaccount_id } : {}),
      })
        .then((res) => {
          const list = res?.data?.data?.data || res?.data?.data || [];
          setServiceList(Array.isArray(list) ? list : []);
        })
        .catch((err) => {
          console.error("Failed to fetch service types", err);
          setServiceList([]);
        });
    }
  }, [openDialog, demoaccount_id]);

  const maxFileSize = 5242880;

  const {
    getRootProps: rootForIcon,
    getInputProps: inputForIcon,
    open: openForIcon,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          formik.setFieldValue("iconFile", file);
          formik.setFieldValue("icon", reader.result);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const getDetails = useCallback(() => {
    if (!id) return;
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setDetails(res?.data?.data);
      } else {
        setDetails(null);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };
    AppIconapi.getAppIconById(id).then(onSuccess, onFailure);
  }, [id, dispatch]);

  useEffect(() => {
    if (openDialog && id) {
      getDetails();
    }
  }, [openDialog, id, getDetails]);

  const handleUpdateIcon = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const data = new FormData();
    data.append("name", formValues?.name?.trim());
    if (formValues?.iconFile) {
      data.append("icon", formValues?.iconFile);
    }
    data.append("status", formValues?.status);

    const stList = Array.isArray(formValues?.serviceTypes)
      ? formValues.serviceTypes
      : [];
    if (stList.length > 0) {
      stList.forEach((st) => {
        const id = st?._id || st;
        if (id && typeof id === "string") {
          data.append("serviceTypes", id);
        }
      });
    } else {
      data.append("serviceTypes", "");
    }

    if (formValues?.primaryservicetype) {
      data.append("primaryservicetype", formValues.primaryservicetype);
    } else {
      data.append("primaryservicetype", "");
    }

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: "App Icon Details Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );

        formik.resetForm();
        setDetails(null);
        handleClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to update data",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    AppIconapi.updateAppIcon(id, data).then(onSuccess, onFailure);
  };

  useEffect(() => {
    if (!_.isEmpty(details)) {
      formik.setFieldValue("name", details?.name || "");
      formik.setFieldValue("icon", details?.icon || "");
      formik.setFieldValue("status", details?.status !== undefined ? details?.status : 1);
      let rawSt = details?.serviceTypes || [];
      if (typeof rawSt === "string") {
        try { rawSt = JSON.parse(rawSt); } catch (e) {}
      }
      if (Array.isArray(rawSt) && rawSt.length === 1 && typeof rawSt[0] === "string" && rawSt[0].trim().startsWith("[")) {
        try { rawSt = JSON.parse(rawSt[0]); } catch (e) {}
      }
      const selectedServiceTypeIds = (Array.isArray(rawSt) ? rawSt : []).map(
        (st) => st?._id || st
      );
      formik.setFieldValue("serviceTypes", selectedServiceTypeIds);
      formik.setFieldValue("primaryservicetype", details?.primaryservicetype || "");
    }
  }, [details]);

  const formik = useFormik({
    initialValues: {
      name: "",
      icon: "",
      iconFile: null,
      serviceTypes: [],
      primaryservicetype: "",
      status: 1,
    },
    validationSchema: yup.object({
      name: yup.string().required("Icon Name is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleUpdateIcon,
  });

  const handleModalClose = () => {
    formik.resetForm();
    setDetails(null);
    handleClose();
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleModalClose}
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.headerBar}>
        <Typography className={classes.headerTitle}>Update App Icon</Typography>
        <IconButton aria-label="close" onClick={handleModalClose} className={classes.closeIconBtn}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.contentContainer}>
        <Typography className={classes.sectionTitle}>Icon Image</Typography>

        <Box {...rootForIcon()} onClick={openForIcon} className={classes.uploadBox}>
          <input {...inputForIcon()} />
          {formik?.values?.icon ? (
            <Box
              component="img"
              src={formik.values.icon}
              alt="Icon preview"
              className={classes.previewImage}
            />
          ) : (
            <>
              <FileUploadOutlinedIcon className={classes.uploadIcon} />
              <Typography className={classes.uploadText}>
                Click to upload icon image
              </Typography>
            </>
          )}
        </Box>

        <Typography className={classes.fieldLabel}>Icon Name</Typography>
        <TextField
          name="name"
          id="name"
          placeholder="Enter icon name"
          value={formik?.values?.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik?.errors?.name && formik?.touched?.name)}
          helperText={
            formik?.errors?.name && formik?.touched?.name
              ? formik?.errors?.name
              : ""
          }
          fullWidth
          variant="outlined"
          className={classes.textField}
        />

        <Typography className={classes.fieldLabel}>Service Types</Typography>
        <FormControl fullWidth className={classes.textField}>
          <Select
            multiple
            displayEmpty
            name="serviceTypes"
            value={formik.values.serviceTypes || []}
            onChange={formik.handleChange}
            size="small"
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return (
                  <span style={{ color: "#9ca3af", fontSize: "14px" }}>
                    Select service types
                  </span>
                );
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((val) => {
                    const matched = serviceList.find(
                      (s) => (s?._id || s) === (val?._id || val)
                    );
                    return (
                      <Chip
                        key={val?._id || val}
                        label={matched?.serviceName || val?.serviceName || val}
                        size="small"
                        sx={{
                          backgroundColor: "#f3f4f6",
                          color: "#1f2937",
                          fontSize: "12px",
                          fontWeight: 500,
                          borderRadius: "6px",
                          height: "24px",
                        }}
                      />
                    );
                  })}
                </Box>
              );
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 250,
                },
              },
            }}
          >
            {serviceList.length === 0 ? (
              <MenuItem disabled value="">
                <em>No service types available</em>
              </MenuItem>
            ) : (
              serviceList.map((service) => {
                const isChecked = (formik.values.serviceTypes || []).some(
                  (val) => (val?._id || val) === service._id
                );
                return (
                  <MenuItem key={service._id} value={service._id}>
                    <Checkbox checked={isChecked} size="small" sx={{ p: 0.5, mr: 1 }} />
                    <ListItemText
                      primary={service.serviceName}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    />
                  </MenuItem>
                );
              })
            )}
          </Select>
        </FormControl>

        <Typography className={classes.fieldLabel}>Primary Service Type</Typography>
        <FormControl fullWidth className={classes.textField}>
          <Select
            displayEmpty
            name="primaryservicetype"
            value={formik.values.primaryservicetype || ""}
            onChange={formik.handleChange}
            size="small"
          >
            <MenuItem value="">
              <em>Select Primary Service Type</em>
            </MenuItem>
            {serviceList.map((service) => (
              <MenuItem key={service._id} value={service._id}>
                {service.serviceName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography className={classes.fieldLabel}>Status</Typography>
        <FormControl fullWidth className={classes.textField}>
          <Select
            name="status"
            value={formik.values.status}
            onChange={formik.handleChange}
            size="small"
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>

        <Box className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleModalClose} className={classes.cancelBtn}>
            Close
          </Button>
          <Button variant="contained" onClick={formik?.handleSubmit} className={classes.submitBtn}>
            Update Icon
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AppIconUpdate;
